import {
  type DatabaseProjectionQuery,
  type DatabaseProjectionSource,
  type DataSourceCellsWindow,
  type DataSourceCellsWindowRequest,
  type DataSourceRowsWindow,
  type PropertyMetaConfig,
} from '@blocksuite/data-view';
import {
  assertDatabaseCellWithinTypeLimit,
  DatabaseShardDirectoryPartsMissingError,
  databaseShardsMayContainRow,
  matchesDatabaseSearchText,
  normalizeDatabaseSearchQuery,
} from '@streamlain/database-identity';

import { type DataSourceWriteActor } from './data-source-contracts.js';
import {
  databaseComputePort,
  measureDatabaseCompute,
} from './data-source-crdt-compute.js';
import { emptyDatabaseProjection } from './data-source-projection.js';
import type { DatabaseSourceRuntime } from './data-source-runtime-context.js';
import { writePreparedShardedCellsToLocalDocs } from './data-source-shard-writing.js';
import {
  normalizeDatabasePropertyWindowRequest,
  SCHEMA_LOCKED_INVALID_CELL_VALUE,
} from './data-source-values.js';

export function _runCapture(this: DatabaseSourceRuntime): void {
  if (this._disposed) {
    return;
  }
  if (this._batch) {
    return;
  }

  this._batch = requestAnimationFrame(() => {
    this.captureStorageSync();
    this._batch = 0;
  });
}

export function getRowsWindow(
  this: DatabaseSourceRuntime,
  request: {
    offset?: number;
    limit: number;
    owner?: string;
  }
): DataSourceRowsWindow {
  return this.getShardedRowsWindow(request);
}

export function getProjectionSource(
  this: DatabaseSourceRuntime,
  query: DatabaseProjectionQuery
): DatabaseProjectionSource {
  let directory;
  try {
    directory = this.getLoadedShardDirectory(0);
  } catch (error) {
    return emptyDatabaseProjection(
      query,
      false,
      error instanceof Error
        ? error.message
        : 'The database directory could not be read.'
    );
  }
  // Rows of the loaded directory parts; the source is complete once every
  // part and every shard is loaded, so it requests the parts still missing.
  if (directory.missingParts.length) this.getCompleteShardDirectory();
  const manifest = directory.manifest;

  // Sharded projections read the materialized row cache directly instead of
  // sourceRowIds$. Track its revision explicitly so computed consumers (for
  // example grouped Kanban lanes) see rows and group keys added by local or
  // remote shard updates without requiring a view reload.
  this._shardedRowsRevision.value;
  if (query.scope === 'whole-database') this.materializeAllShardedRows();
  const integrityError = this._rowShardIntegrityError.value;
  if (integrityError) {
    return {
      complete: false,
      diagnostics: [
        {
          code: 'partial-source',
          message: integrityError,
        },
      ],
      freshness: 'stale',
      rowIds: [],
      source: 'local-window',
      syncState: 'partial',
    };
  }
  const complete =
    !directory.missingParts.length &&
    manifest.shards.every(
      shard =>
        this._shardedRowsByDocId.get(shard.serverDocId)?.rows.length ===
        shard.rowCount
    );
  return {
    complete,
    diagnostics: [],
    freshness: complete ? 'live' : 'stale',
    rowIds: manifest.shards.flatMap(
      shard =>
        this._shardedRowsByDocId
          .get(shard.serverDocId)
          ?.rows.map(row => row.id) ?? []
    ),
    source: complete ? 'local-shards' : 'local-window',
    syncState: complete ? 'live' : 'partial',
  };
}

export function getSearchCandidateRowIds(
  this: DatabaseSourceRuntime,
  rowIds: readonly string[],
  searchText: string,
  propertyIds: readonly string[]
): readonly string[] {
  const needle = normalizeDatabaseSearchQuery(searchText);
  if (!needle) return rowIds;
  return rowIds.filter(rowId =>
    matchesDatabaseSearchText(
      this.getShardedRowSearchText(rowId, propertyIds),
      needle
    )
  );
}

export function getCellsWindow(
  this: DatabaseSourceRuntime,
  request: DataSourceCellsWindowRequest
): DataSourceCellsWindow {
  const rowsWindow = this.getShardedRowsWindow(request);
  const propertySource =
    request.propertyIds ?? this.getBoundedPropertyIdsWindow().propertyIds;
  const { propertyOffset, propertyLimit, propertyIds } =
    normalizeDatabasePropertyWindowRequest(request, propertySource);
  const cells: Record<string, Record<string, unknown>> = {};

  for (const rowId of rowsWindow.rowIds) {
    const rowCells: Record<string, unknown> = {};
    for (const propertyId of propertyIds) {
      rowCells[propertyId] = this.cellValueGetFromLoadedRows(rowId, propertyId);
    }
    cells[rowId] = rowCells;
  }

  return {
    offset: rowsWindow.offset,
    limit: rowsWindow.limit,
    propertyOffset,
    propertyLimit,
    totalRowCount: rowsWindow.totalCount,
    totalColumnCount: this.columnCount$.value,
    rowIds: rowsWindow.rowIds,
    propertyIds,
    cells,
  };
}

export function getLogicalRowIdsSnapshot(
  this: DatabaseSourceRuntime
): string[] {
  // Effects reading the rows run again once directory parts arrive.
  this._shardedRowsRevision.value;
  this.materializeAllShardedRows();
  // Callers that can wait check the complete directory first; a partial
  // list is never returned as the database rows.
  const directory = this.getCompleteShardDirectory();
  if (directory.status !== 'ready')
    throw new DatabaseShardDirectoryPartsMissingError(
      this.databaseId,
      directory.partDocIds
    );
  return this._shardedRowIds.slice();
}

export function rowExists(this: DatabaseSourceRuntime, rowId: string): boolean {
  const directory = this.getCompleteShardDirectory();
  const manifest =
    directory.status === 'ready' ? directory.manifest : undefined;
  // Fingerprints rule a row out without loading; a possible row is
  // confirmed only by the loaded shard documents. While directory parts
  // load, a row not loaded yet is not found yet: callers pair this with
  // the loading state of the row data.
  if (manifest && !databaseShardsMayContainRow(manifest.shards, rowId))
    return false;
  if (!this._shardedRowsById.has(rowId))
    this.loadShardedRowFromExistingDocs(rowId);
  return this._shardedRowsById.has(rowId);
}

export function newPropertyName(
  this: DatabaseSourceRuntime,
  prefix = 'Column'
): string {
  let i = 1;
  const hasSameName = (name: string) => {
    return this._model.props.columns$.value.some(
      column => column.name === name
    );
  };
  while (true) {
    let name = i === 1 ? prefix : `${prefix} ${i}`;
    if (!hasSameName(name)) {
      return name;
    }
    i++;
  }
}

export function cellValueChange(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown,
  actor: DataSourceWriteActor = 'user'
): void {
  this.assertFileIndexCellMutationAllowed(propertyId, actor);
  // sl-d7zo.7.4: Row-sharded databases write cell values into the owning
  // row-shard Yjs doc.
  this.cellValueChangeSharded(rowId, propertyId, value);
}

export function cellValuesChange(
  this: DatabaseSourceRuntime,
  rowId: string,
  values: Readonly<Record<string, unknown>>,
  actor: DataSourceWriteActor = 'user'
): void {
  const entries = Object.entries(values);
  if (entries.length === 0) return;
  const cells = entries.flatMap(([propertyId, value]) => {
    const cell = this.resolveShardedCellForWrite(
      rowId,
      propertyId,
      value,
      actor
    );
    return cell ? [[propertyId, cell] as const] : [];
  });
  if (cells.length === 0) return;

  void writePreparedShardedCellsToLocalDocs.call(
    this,
    new Map([[rowId, Object.fromEntries(cells)]])
  );
}

export function cellValueChangeSharded(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  value: unknown
): void {
  const type = this.propertyTypeGet(propertyId);
  if (type == null) {
    return;
  }
  const propertyData = this.propertyDataGet(propertyId);
  const newValue = this.normalizeSchemaLockedSelectValue(
    type,
    propertyData,
    value
  );
  if (newValue === SCHEMA_LOCKED_INVALID_CELL_VALUE) {
    return;
  }

  const resolvedValue = this.resolveRawValueForProperty(
    rowId,
    propertyId,
    type,
    propertyData,
    newValue
  );
  const persistedValue = this.serializeRawValueForProperty(
    type,
    propertyData,
    resolvedValue
  );

  assertDatabaseCellWithinTypeLimit({
    source: 'Database editor',
    columnType: type,
    rowId,
    columnId: propertyId,
    value: persistedValue,
  });
  this.writeShardedCellToLocalDoc(rowId, propertyId, persistedValue);
}

export function resolveRawValueForProperty(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string,
  type: string,
  propertyData: Record<string, unknown>,
  newValue: unknown
): unknown {
  const meta = this.propertyMetaGet(type);
  const rawSetter = meta?.config.rawValue.setValue;
  // The default setter only replaces the value. Reading/cloning the previous
  // cell would repeatedly validate the whole shard manifest for every input
  // cell, without contributing to the result. Custom setters still receive
  // the detached current value below.
  if (!rawSetter) return newValue;
  const compute = databaseComputePort(this._model.store.workspace);
  const currentValue = measureDatabaseCompute(
    compute,
    'cell-current-read',
    1,
    () => this.cellValueGet(rowId, propertyId)
  );
  // Raw setters can mutate Text in place. A queued writer must not mutate a
  // cached reactive cell and trigger a later rAF save of the stale value.
  const fromJson = meta?.config.rawValue.fromJson;
  const value =
    fromJson && currentValue != null
      ? fromJson({
          data: propertyData,
          dataSource: this.dataSource,
          value: this.serializeRawValueForProperty(
            type,
            propertyData,
            currentValue
          ),
        })
      : currentValue;
  let resolvedValue: unknown = newValue;
  rawSetter({
    value,
    data: propertyData,
    dataSource: this.dataSource,
    newValue,
    setValue: nextValue => {
      resolvedValue = nextValue;
    },
  });
  return resolvedValue;
}

export function serializeRawValueForProperty(
  this: DatabaseSourceRuntime,
  type: string,
  propertyData: Record<string, unknown>,
  value: unknown
): unknown {
  const rawValue = this.propertyMetaGet(type)?.config.rawValue;
  const rawToJson = rawValue?.toJson;
  const rawFromJson = rawValue?.fromJson;
  // `toJson` is the semantic view value used by filters/renderers; for types
  // without a matching `fromJson` it is not a persistence codec. Attachment
  // cells, for example, expose file names as JSON but must keep the file
  // record in row-shard storage.
  if (!rawFromJson) {
    return value;
  }
  return rawToJson
    ? rawToJson({
        value,
        data: propertyData,
        dataSource: this.dataSource,
      })
    : value;
}

export function normalizeSchemaLockedSelectValue(
  this: DatabaseSourceRuntime,
  type: string,
  data: Record<string, unknown>,
  value: unknown
): unknown {
  if (this._model.props.schemaLocked !== true) return value;
  if (type !== 'select' && type !== 'multi-select') return value;
  const options = Array.isArray(data.options)
    ? (data.options as Array<{ id?: unknown; value?: unknown }>)
    : [];
  const resolve = (
    raw: unknown
  ): string | typeof SCHEMA_LOCKED_INVALID_CELL_VALUE => {
    const text = String(raw);
    const option = options.find(
      option => option.id === text || option.value === text
    );
    return typeof option?.id === 'string'
      ? option.id
      : SCHEMA_LOCKED_INVALID_CELL_VALUE;
  };
  if (type === 'select') {
    if (value === null || value === undefined || value === '') return value;
    return resolve(value);
  }
  const values = Array.isArray(value)
    ? value
    : value === null || value === undefined || value === ''
      ? []
      : [value];
  const resolved = values.map(resolve);
  return resolved.includes(SCHEMA_LOCKED_INVALID_CELL_VALUE)
    ? SCHEMA_LOCKED_INVALID_CELL_VALUE
    : resolved;
}

export function cellValueGet(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): unknown {
  if (!this._shardedRowsById.has(rowId)) {
    this.loadShardedRowFromExistingDocs(rowId);
  }
  return this.cellValueGetFromLoadedRows(rowId, propertyId);
}

export function cellValueGetFromLoadedRows(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): unknown {
  const shardedCell = this.getShardedCellValue(rowId, propertyId);
  if (shardedCell.found) {
    return shardedCell.value;
  }
  if (!this._shardedRowsById.has(rowId)) {
    return undefined;
  }
  return this.cellDefaultValueGet(rowId, propertyId);
}

export function cellDefaultValueGet(
  this: DatabaseSourceRuntime,
  rowId: string,
  propertyId: string
): unknown {
  if (this.isSpacialProperty(propertyId)) {
    return this.spacialValueGet(rowId, propertyId, propertyId);
  }
  const type = this.propertyTypeGet(propertyId);
  if (!type) {
    return;
  }
  if (this.isSpacialProperty(type)) {
    return this.spacialValueGet(rowId, propertyId, type);
  }
  const meta = this.propertyMetaGet(type);
  if (!meta) {
    return;
  }
  return this.normalizeRawCellValue(meta, meta.config.rawValue.default());
}

export function normalizeRawCellValue(
  this: DatabaseSourceRuntime,
  meta: PropertyMetaConfig,
  rawValue: unknown
): unknown {
  const result = meta.config.rawValue.schema.safeParse(rawValue);
  if (result.success) {
    return result.data;
  }
  return;
}
